"use client";

import { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Eye, EyeOff, CheckCircle2, Loader2, ArrowLeft } from "lucide-react";


const perks = [
    // "14-day free trial — no credit card required",
    "500+ datasets available on day one",
    "Setup in under 5 minutes",
    "Cancel anytime, no lock-in",
];

export default function Signup() {
    const router = useRouter();
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [form, setForm] = useState({
        first_name: "",
        last_name: "",
        email: "",
        company_name: "",
        phone_number: "",
        password: "",
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        // Basic client-side validation
        const required: (keyof typeof form)[] = [
            "first_name",
            "last_name",
            "email",
            "company_name",
            "password",
        ];
        const missing = required.filter((k) => !form[k].trim());
        if (missing.length) {
            setError("Please fill in all required fields.");
            return;
        }
        if (form.password.length < 8) {
            setError("Password must be at least 8 characters.");
            return;
        }

        setLoading(true);
        try {
            const res = await fetch(
                "https://api.medtechdata.us/api/agency/register",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        accept: "*/*",
                    },
                    body: JSON.stringify({
                        company_name: form.company_name,
                        first_name: form.first_name,
                        last_name: form.last_name,
                        email: form.email,
                        password: form.password,
                        phone_number: form.phone_number || undefined,
                    }),
                }
            );

            const data = await res.json();

            if (!res.ok) {
                const msg =
                    data?.detail ||
                    data?.message ||
                    data?.error ||
                    "Registration failed. Please try again.";
                setError(typeof msg === "string" ? msg : JSON.stringify(msg));
                return;
            }

            // Persist token if returned on registration
            if (data?.access_token) {
                localStorage.setItem("access_token", data.access_token);
            }
            if (data?.token) {
                localStorage.setItem("access_token", data.token);
            }

            router.push("/dashboard");
        } catch (err) {
            setError("Network error. Please check your connection and try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-background flex">
            {/* Left panel */}
            <div
                className="hidden lg:flex lg:w-5/12 border-r border-border flex-col justify-between p-12 relative"
                style={{
                    backgroundImage: "url('/banner.png')",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
            >
                <div className="absolute inset-0 bg-background/60 backdrop-blur-[2px]" />
                <div className="relative z-10 flex flex-col justify-between h-full">
                    <Link href="/" className="inline-flex">
                        <div className="bg-white/80 backdrop-blur-xl border border-white/40 rounded-2xl p-3 shadow-2xl flex items-center justify-center transition-all hover:bg-white/90">
                            <img
                                src="/logo2.png"
                                alt="Medtechdata"
                                className="h-10 w-auto object-contain"
                            />
                        </div>
                    </Link>

                    <div className="space-y-5">
                        <h2 className="text-3xl font-semibold text-foreground leading-tight">
                            Start building with{" "}
                            <span className="gradient-orange-text">premium data</span> today
                        </h2>

                        <ul className="space-y-3">
                            {perks.map((perk) => (
                                <li key={perk} className="flex items-start gap-3">
                                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                                    <span className="text-sm text-foreground font-medium">
                                        {perk}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="space-y-3">
                        <p className="text-xs text-foreground font-semibold uppercase tracking-wide">
                            Trusted by teams at
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {[
                                "Meridian",
                                "Lumion Co.",
                                "Arcwave",
                                "DataBridge",
                                "Nexora",
                            ].map((co) => (
                                <div
                                    key={co}
                                    className="bg-card/80 backdrop-blur-sm rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground"
                                >
                                    {co}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Right panel — force light background regardless of global theme */}
            <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 bg-[#f7f7f5]">
                <div className="w-full max-w-sm">
                    {/* Top: Prominent Logo */}
                    <div className="flex justify-center mb-6">
                        <Link href="/">
                            <img
                                src="/logo2.png"
                                alt="Medtechdata"
                                className="h-10 sm:h-12 w-auto object-contain"
                            />
                        </Link>
                    </div>

                    {/* Back to Home Button */}
                    <div className="mb-6">
                        <Link
                            href="/"
                            className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-orange-600 transition-colors group"
                        >
                            <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
                            <span>Back to home</span>
                        </Link>
                    </div>

                    <div className="mb-6">
                        <h1 className="text-2xl font-semibold text-gray-900">
                            Create your account
                        </h1>
                        {/* <p className="mt-1 text-sm text-gray-500">
                            Free for 14 days. No credit card required.
                        </p> */}
                    </div>

                    <form className="space-y-4" onSubmit={handleSubmit}>
                        {error && (
                            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                                {error}
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <label className="text-sm font-medium text-gray-700">
                                    First name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    name="first_name"
                                    placeholder="James"
                                    value={form.first_name}
                                    onChange={handleChange}
                                    required
                                    disabled={loading}
                                    className="w-full rounded-xl h-11 px-3 border border-gray-200 bg-white text-gray-900 placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition disabled:opacity-50"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-sm font-medium text-gray-700">
                                    Last name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    name="last_name"
                                    placeholder="Davis"
                                    value={form.last_name}
                                    onChange={handleChange}
                                    required
                                    disabled={loading}
                                    className="w-full rounded-xl h-11 px-3 border border-gray-200 bg-white text-gray-900 placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition disabled:opacity-50"
                                />
                            </div>
                        </div>

                        <div className="space-y-1.5">
                            <label className="text-sm font-medium text-gray-700">
                                Work email <span className="text-red-500">*</span>
                            </label>
                            <input
                                name="email"
                                type="email"
                                placeholder="james@agency.com"
                                value={form.email}
                                onChange={handleChange}
                                required
                                disabled={loading}
                                className="w-full rounded-xl h-11 px-3 border border-gray-200 bg-white text-gray-900 placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition disabled:opacity-50"
                            />
                        </div>

                        <div className="space-y-1.5">
                            <label className="text-sm font-medium text-gray-700">
                                Company <span className="text-red-500">*</span>
                            </label>
                            <input
                                name="company_name"
                                placeholder="Acme Agency"
                                value={form.company_name}
                                onChange={handleChange}
                                required
                                disabled={loading}
                                className="w-full rounded-xl h-11 px-3 border border-gray-200 bg-white text-gray-900 placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition disabled:opacity-50"
                            />
                        </div>

                        <div className="space-y-1.5">
                            <label className="text-sm font-medium text-gray-700">
                                Phone number{" "}
                                <span className="text-gray-400 text-xs">(optional)</span>
                            </label>
                            <input
                                name="phone_number"
                                type="tel"
                                placeholder="+1234567890"
                                value={form.phone_number}
                                onChange={handleChange}
                                disabled={loading}
                                className="w-full rounded-xl h-11 px-3 border border-gray-200 bg-white text-gray-900 placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition disabled:opacity-50"
                            />
                        </div>

                        <div className="space-y-1.5">
                            <label className="text-sm font-medium text-gray-700">
                                Password <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <input
                                    name="password"
                                    type={showPassword ? "text" : "password"}
                                    placeholder="Min. 8 characters"
                                    value={form.password}
                                    onChange={handleChange}
                                    required
                                    minLength={8}
                                    disabled={loading}
                                    className="w-full rounded-xl h-11 px-3 pr-10 border border-gray-200 bg-white text-gray-900 placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition disabled:opacity-50"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                    tabIndex={-1}
                                >
                                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full h-11 mt-2 rounded-xl font-medium text-white text-sm transition-opacity hover:opacity-90 disabled:opacity-60 flex items-center justify-center"
                            style={{ background: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)" }}
                        >
                            {loading ? (
                                <span className="flex items-center gap-2">
                                    <Loader2 size={16} className="animate-spin" />
                                    Creating account…
                                </span>
                            ) : (
                                "Create Free Account"
                            )}
                        </button>
                    </form>

                    <p className="mt-3 text-center text-xs text-gray-400">
                        By signing up, you agree to our{" "}
                        <a href="#" className="text-orange-500 hover:underline">
                            Terms of Service
                        </a>{" "}
                        and{" "}
                        <a href="#" className="text-orange-500 hover:underline">
                            Privacy Policy
                        </a>
                        .
                    </p>

                    <p className="mt-5 text-center text-sm text-gray-500">
                        Already have an account?{" "}
                        <Link
                            href="/login"
                            className="font-medium text-orange-500 hover:text-orange-600 transition-colors"
                        >
                            Sign in
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}